import { useEffect, useMemo, useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  ModuleTypes,
  PaymentStatus,
  RegisterButtonLocation,
} from "@repo/database";
import { createFileRoute } from "@tanstack/react-router";
import { v4 as uuidv4 } from "uuid";

import type { CartItem } from "~/providers/cart-provider";
import { FunctionButtons } from "~/components/button/function-buttons";
import { CashRegisterCart } from "~/components/cash-register/cart";
import { CategoryList } from "~/components/cash-register/category-list";
import { ProductList } from "~/components/cash-register/product-list";
import LoadingSpinner from "~/components/miscellaneous/loading-spinner";
import OrderGroupSize from "~/components/order/order-group-size";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "~/components/ui/drawer";
import { RegisterButton } from "~/components/ui/register-button";
import { ScrollArea } from "~/components/ui/scroll-area";
import { useConfiguration } from "~/hooks/use-configuration";
import { useModule } from "~/hooks/use-module";
import { useOrder } from "~/hooks/use-order";
import { useProduct } from "~/hooks/use-product";
import { useTable } from "~/hooks/use-table";
import { cn } from "~/lib/utils";
import { useCartManager } from "~/managers/use-cart-manager";
import { useOrderUpdates } from "~/managers/use-order-manager";
import { CartProvider } from "~/providers/cart-provider";
import { useOrderStore } from "~/stores/use-order-store";
import { useRegisterStore } from "~/stores/use-register-store";
import { useTableStore } from "~/stores/use-table-store";

interface CashRegisterSearch {
  orderId?: string;
  tableId?: string;
}

export const Route = createFileRoute("/_main/cash-register/")({
  component: Register,
  validateSearch: (search: Record<string, unknown>): CashRegisterSearch => {
    return {
      orderId: search.orderId as string,
      tableId: search.tableId as string,
    };
  },
});

function Register() {
  console.log("CASH REGISTER RENDER");
  const { tableId, orderId } = Route.useSearch();


  const { orders } = useOrder();

  const { modules } = useModule();

  const { tables } = useTable();

  const { cashRegisterConfiguration } = useConfiguration();

  const { refreshTableOrders } = useOrderUpdates();

  const [groupsizeOpen, setGroupsizeOpen] = useState(false);

  const { products, productCategories } = useProduct();

  const { addToCart } = useCartManager();

  const order = useMemo(
    () => ({
      id: uuidv4(),
      creditId: undefined,
      payments: [],
      tableId: undefined,
      items: [],
      labelIds: [],
      name: undefined,
      ticketPrinted: false,
      partySize: undefined,
      bookingId: undefined,
      currentRound: 1,
      total: 0,
      amountPaid: 0,
      paymentStatus: PaymentStatus.pending,
      updatedAt: new Date(),
      createdAt: new Date(),
    }),
    [],
  );

  const ordersOnTable = orders.filter((order) => order.tableId === tableId);

  const {
    selectedCategoryId,
    mobileProductsCollapsed,
    setMobileProductsCollapsed,
  } = useRegisterStore();

  const setCurrentOrder = useOrderStore((state) => state.setCurrentOrder);

  const [loadingOrders, setLoadingOrders] = useState(true);

  const setCurrentTable = useTableStore((state) => state.setCurrentTable);

  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);

  const selectedCategory = productCategories.find(
    (category) => category.id === selectedCategoryId,
  );


  const handleAddToCart = (item: CartItem) => {
    void addToCart(item);
  };

  const selectedTableId = tableId;

  const selectedTable = tables.find((t) => t.id === selectedTableId);

  useEffect(() => {
    setMobileProductsCollapsed(false);
  }, [setMobileProductsCollapsed]);

  useEffect(() => {
  

    refreshTableOrders(tableId)
      .then(() => setLoadingOrders(false))
      .catch((e) => {
        console.error(e);
        setLoadingOrders(false);
      });
  }, [tableId])

  useEffect(() => {
    if (!tableId) {
      setLoadingOrders(false);
      return;
    }

    if (
      !loadingOrders &&
      ordersOnTable.length === 0 &&
      selectedTable &&
      selectedTable.maxSeats > 0 &&
      cashRegisterConfiguration.tableInfoPopup === true
    ) {
      setGroupsizeOpen(true);
    }
  }, [
    loadingOrders,
    selectedTable,
    cashRegisterConfiguration.tableInfoPopup,
    ordersOnTable.length,
    tableId,
  ]);

  useEffect(() => {
    setCurrentTable(selectedTable);

    if (ordersOnTable.find((order) => order.id === orderId)) {
      setCurrentOrder(ordersOnTable.find((order) => order.id === orderId));
    } else {
      setCurrentOrder(ordersOnTable[0] ?? order);
    }
  }, [
    order,
    orderId,
    ordersOnTable,
    selectedTable,
    setCurrentOrder,
    setCurrentTable,
  ]);

  if (!modules.map((m) => m.type).includes(ModuleTypes.cash_register)) {
    return null;
  }

  return (
    <main className="flex w-full flex-col @container h-full">
      {loadingOrders && (
        <div className="inset-0 absolute z-[999] bg-black/80 flex items-center justify-center">
          <span className="text-2xl text-center">
            <LoadingSpinner size={100} color="white" />
          </span>
        </div>
      )}
      <div className="flex h-full flex-row overflow-auto">
        <div className={cn("border-r max-w-[400px] w-full @container")}>
          <CartProvider>
            <OrderGroupSize
              open={groupsizeOpen}
              onClose={(updatedOrder) => {
                if (updatedOrder) {
                  setCurrentOrder(updatedOrder);
                }
                setGroupsizeOpen(false);
              }}
              order={order}
            />
            <CashRegisterCart
              orders={ordersOnTable.length === 0 ? [order] : ordersOnTable}
              table={selectedTable}
            />
          </CartProvider>
        </div>
        <div
          className={cn(
            "transition-all hide-scrollbar hidden overflow-auto border-r min-w-[200px] lg:block",
          )}
        >
          <CategoryList />
        </div>
        <div
          className={cn(
            "flex flex-col justify-between min-w-[140px] flex-1 @container",
            mobileProductsCollapsed && "hidden",
          )}
        >
          <ProductList
            onClick={(item) => {
              handleAddToCart(item);
            }}
            categoryId={selectedCategory?.id}
            products={products}
          />
          <div className="border-t-2">
            <Drawer
              onOpenChange={setCategoryDrawerOpen}
              open={categoryDrawerOpen}
            >
              <DrawerTrigger asChild className="flex w-full h-14 lg:hidden">
                <RegisterButton
                  className="rounded-none h-14"
                  color={selectedCategory?.color ?? undefined}
                >
                  {selectedCategory?.name}
                </RegisterButton>
              </DrawerTrigger>
              <DrawerContent>
                <VisuallyHidden>
                  <DrawerHeader>
                    <DrawerTitle></DrawerTitle>
                    <DrawerDescription></DrawerDescription>
                  </DrawerHeader>
                </VisuallyHidden>
                <ScrollArea className="max-h-[80vh] p-6 max-w-lg mx-auto w-full">
                  <div className="h-[600px]">
                    <CategoryList
                      onCategorySelect={() => {
                        setCategoryDrawerOpen(false);
                      }}
                    />
                  </div>
                </ScrollArea>
              </DrawerContent>
            </Drawer>
          </div>
        </div>
        <div className="hidden h-full w-32 flex-col md:flex">
          <FunctionButtons
            location={RegisterButtonLocation.sidebar_right}
            orientation="vertical"
          />
        </div>
      </div>
      <div className="flex w-full flex-row gap-0.5 border-t">
        <div className="overflow-auto w-full">
          <FunctionButtons location={RegisterButtonLocation.footer} />
        </div>
      </div>
    </main>
  );
}
